import { useCallback, useEffect, useRef, useState } from 'react';

import { HighchartsReactRefObject } from 'highcharts-react-official';
import { useEvent } from 'react-use';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import { ExtendedChart, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';

const useRegularChart = ({
  chartSeries
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
}) => {
  const [chart, setChart] = useState<ExtendedChart | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const chartComponentRef = useRef<HighchartsReactRefObject>(undefined);
  const chartContainerRef = useRef<HTMLDivElement>(undefined);

  const handleResize = useCallback(() => {
    const chart = chartComponentRef.current?.chart;
    const container = chartContainerRef.current;

    if (!chart || !container) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Hide chart during resize to avoid visual glitches
      chart.container.style.display = 'none';
      chart.setSize(container.getBoundingClientRect().width);

      // Show chart after resize
      chart.container.style.display = 'block';
    }, 100);
  }, []);

  useEvent('resize', handleResize, window);

  const { setChartSeries, chartRef } = useChartContext();

  const lastChartRefInstance = useRef<HighchartsReactRefObject | undefined>(undefined);

  useEffect(() => {
    const currentRef = chartComponentRef.current;
    if (currentRef && currentRef !== lastChartRefInstance.current) {
      lastChartRefInstance.current = currentRef;
      setChart(currentRef.chart as ExtendedChart);
      chartRef.current = currentRef;
    }
  });

  useEffect(() => {
    setChartSeries(chartSeries);
  }, [chartSeries]);

  return { chart, chartComponentRef, chartContainerRef };
};

export default useRegularChart;
